import {
  Calculate as CalculateIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchAndSort } from "../../hooks/useSearchAndSort";
import ElicalLeadDetailsModal from "../../components/leads/ElicalLeadDetailsModal";
import { getLeads, updateLeadStatus, deleteLead } from "../../services/leadService";
import DateRangePickerWrapper from "../../components/common/DateRangePickerWrapper";
import CommonTable from "../../components/common/CommonTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  formatToISODateTime,
  showToast,
} from "../../components/common/Util/Util";
import TalkToAdvisorLeadForm from "../../components/leads/TalkToAdvisorLeadForm";
import EligibilityCalculatorModal from "../../components/leads/EligibilityCalculatorModal";
import DeleteConfirmation from "../../components/users/DeleteConfirmation";
import { useSelector } from "react-redux";
import { userPermissions } from "../../Store/userSlice";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { leadsColumns, withActionsColumn } from "../../constants/tableColumns";

const TabButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: 50,
  padding: "8px 20px",
  fontWeight: 600,
  textTransform: "none",
  fontSize: "14px",
  transition: "all 0.3s ease",
  minWidth: 160,
  backgroundColor: selected ? theme.palette.primary.main : "transparent",
  color: selected ? "white" : theme.palette.text.primary,
  "&:hover": {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : "rgba(0, 0, 0, 0.04)",
  },
}));

const TabContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "8px",
  padding: "6px",
  backgroundColor: "#f0f1ff",
  borderRadius: 50,
  width: "fit-content",
}));

const Leads = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userPermission = useSelector(userPermissions);
  
  const FIELD_MAP = new Map([
    ['date', 'createDatetime'],
    ['phone', 'mobileNumber'],
    ['status', 'status'],
    ['source', 'source'],
    ['annualProfit', 'annualProfit'],
    ['businessType', 'businessType'],
    ['entityName', 'entityName'],
    ['unsecuredProducts', 'unsecuredProducts'],
    ['revenue', 'revenue'],
    ['loanAmount', 'loanAmount'],
    ['businessAge', 'businessAge'],
    ['fundsUsed', 'fundsUsed'],
    ['securedProduct', 'securedProduct'],
    ['emiHistory', 'emiHistory'],
    ['tenure', 'tenure'],
    ['approvalChance', 'approvalChance'],
    ['score', 'score'],
    ['entityType', 'entityType'],
  ]);

  const getSortByField = (columnId) => FIELD_MAP.get(columnId) || columnId;

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    qualified: 0,
    eligible: 0,
    notEligible: 0,
    pending: 0,
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [dateRange, setDateRange] = React.useState([null, null]);
  const [showTalkToAdvisorForm, setShowTalkToAdvisorForm] = useState(false);
  const [showEligibilityCalculator, setShowEligibilityCalculator] =
    useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [permission, setPermissions] = useState({});
  const { searchQuery, setSearchQuery, handleSort, searchAndSortParams, sortBy, order } = useSearchAndSort(500, `leads-${activeTab}`);
  useEffect(() => {
    const moduleList = userPermission?.map((e) => e?.module);
    if (moduleList && moduleList.length > 0) {
      const hasLeadsPermission = moduleList.includes("leads");
      if (!hasLeadsPermission) {
        showToast("You do not have permission to access Leads", "error");
        navigate("/dashboard");
      } else {
        setPermissions(JSON.parse(userPermission.find(e => e.module === "leads")?.permission)
          || null);
      }
    }
  }, [userPermission, navigate]);

  const getUserFriendlyLabel = (fieldName) => {
    const formattedLabel = fieldName
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    const unitFieldMappings = {
      Tenure: "Tenure (in Years)",
      "Loan Amount": "Loan Amount (in Lakhs)",
      "Annual Profit": "Annual Profit (in Lakhs)",
      Revenue: "Revenue (in Lakhs)",
      "Business Age": "Business Age (in Years)",
      tenure: "Tenure (in Years)",
      "loan amount": "Loan Amount (in Lakhs)",
      "annual profit": "Annual Profit (in Lakhs)",
      revenue: "Revenue (in Lakhs)",
      "business age": "Business Age (in Years)",
      loan_amount: "Loan Amount (in Lakhs)",
      annual_profit: "Annual Profit (in Lakhs)",
      business_age: "Business Age (in Years)",
    };

    if (unitFieldMappings[formattedLabel]) {
      return unitFieldMappings[formattedLabel];
    }

    const partialMatches = {
      "tenure": "Tenure (in Years)",
      "loan amount": "Loan Amount (in Lakhs)",
      "annual profit": "Annual Profit (in Lakhs)",
      "revenue": "Revenue (in Lakhs)",
      "business age": "Business Age (in Years)",
    };

    for (const [pattern, label] of Object.entries(partialMatches)) {
      if (formattedLabel.toLowerCase().includes(pattern)) {
        return label;
      }
    }

    const databaseFieldMappings = {
      "Total Sales": "Revenue (in Lakhs)",
      "Outstanding Default": "Payment Status",
      "Total Loan": "Loan Amount (in Lakhs)",
      "Interest Rate": "Interest Rate",
      "Monthly Payment": "Monthly Payment",
      "Property Type": "Property Type",
      "Property Value": "Property Value",
      "Roof Type": "Roof Type",
      "Roof Age": "Roof Age",
      "First Name": "First Name",
      "Last Name": "Last Name",
      "Phone Number": "Phone",
      "Email Address": "Email",
      Longitude: "Location Coordinates",
      Latitude: "Location Coordinates",
      "End Use": "Purpose",
      "Energy Usage": "Energy Consumption",
      "Average Bill": "Avg. Monthly Bill",
      "Application Status": "Status",
      "Eligibility Score": "Eligibility",
      "Credit Score": "Credit",
      Income: "Annual Income",
    };

    for (const [dbField, friendlyLabel] of Object.entries(databaseFieldMappings)) {
      if (formattedLabel.toLowerCase().includes(dbField.toLowerCase())) {
        return friendlyLabel;
      }
    }

    return formattedLabel;
  };

  const hasValidDataForField = useCallback((fieldName) => {
    if (!data || data.length === 0) return false;

    const isGeoLocationField = fieldName.toLowerCase().includes("longitude") ||
      fieldName.toLowerCase().includes("latitude");

    if (!isGeoLocationField) return true;

    return data.some((row) => {
      const fieldValue = row[fieldName];
      const isValidValue = fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== "undefined" &&
        String(fieldValue).toLowerCase() !== "undefined" &&
        fieldValue !== "";

      return isValidValue;
    });
  }, [data]);

  const generateDynamicColumns = useCallback(() => {
    if (activeTab === 0) {
      return withActionsColumn([...leadsColumns.advisorBase], permission);
    } else {
      let dynamicColumns = [...leadsColumns.elicalBase];

      if (data.length > 0) {
        const sampleDataItem = data[0];
        const fieldsToExclude = [
          "id", "lead_id", "lead_uuid", "mobile_number", "mobileNumber",
          "status", "create_datetime", "createDatetime", "phone", "date",
          "actions", "name", "email", "ip", "lead_type",
          "Outstanding_default", "End_use", "outstanding_default", "end_use",
        ];

        Object.keys(sampleDataItem).forEach((fieldKey) => {
          const shouldIncludeField = !fieldsToExclude.includes(fieldKey) &&
            sampleDataItem[fieldKey] !== undefined &&
            hasValidDataForField(fieldKey);

          if (shouldIncludeField) {
            const userFriendlyLabel = getUserFriendlyLabel(fieldKey);

            dynamicColumns.push({
              id: fieldKey,
              label: userFriendlyLabel,
              render: (rowData) => {
                const fieldValue = rowData[fieldKey];
                if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
                  return "-";
                }

                if (typeof fieldValue === "boolean") {
                  return fieldValue ? "Yes" : "No";
                }

                if (Array.isArray(fieldValue)) {
                  return fieldValue.length > 0 ? fieldValue.join(", ") : "-";
                }

                const isGeoField = fieldKey.toLowerCase().includes("longitude") ||
                  fieldKey.toLowerCase().includes("latitude");
                if (isGeoField && (fieldValue === "undefined" || String(fieldValue).toLowerCase() === "undefined")) {
                  return "-";
                }

                return String(fieldValue);
              },
            });
          }
        });
      }

      return withActionsColumn(dynamicColumns, permission);
    }
  }, [activeTab, data, permission, hasValidDataForField]);

  const columns = useMemo(generateDynamicColumns, [generateDynamicColumns]);

  useEffect(() => {
    const pageable = {
      page: page,
      size: rowsPerPage,
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  }, [activeTab]);

  useEffect(() => {
  if(dateRange[0] || dateRange[1]){
    const pageable = {
      page: page,
      size: rowsPerPage,
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  }
  }, [dateRange]);

  useEffect(() => {
    const pageable = {
      page,
      size: rowsPerPage,
      startDate: dateRange[0] ? formatToISODateTime(dateRange[0], "startDate") : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  }, [searchAndSortParams.searchQuery, searchAndSortParams.sortBy, searchAndSortParams.order]);

  const loadData = async (pageable) => {
    setLoading(true);
    setError(null);
    try {
      const leadType = activeTab === 0 ? "advisor" : "elical";
      const sortByField = searchAndSortParams.sortBy ? getSortByField(searchAndSortParams.sortBy) : searchAndSortParams.sortBy;
      const pageableWithSearch = { 
        ...pageable, 
        ...searchAndSortParams,
        ...(sortByField && { sortBy: sortByField })
      };
      const response = await getLeads(leadType, pageableWithSearch);

      if (response && response.responseCode === 200) {
        if (response?.result?.content) {
          const transformedData = transformApiData(
            response?.result?.content,
            leadType
          );
          setData(transformedData);
          setTotalRecords(response?.result?.totalElements ?? transformedData.length ?? 0);
          updateStats(transformedData, leadType);
        } else if (Array.isArray(response?.result)) {
          const transformedData = transformApiData(response?.result, leadType);
          setData(transformedData);
          setTotalRecords(
            response?.rowCount ??
            response?.totalElements ??
            response?.result?.totalElements ??
            transformedData.length ?? 0
          );
          updateStats(transformedData, leadType);
        } else if (Array.isArray(response)) {
          const transformedData = transformApiData(response, leadType);
          setData(transformedData);
          setTotalRecords(transformedData.length ?? 0);
          updateStats(transformedData, leadType);
        } else {
          setData([]);
          resetStats(leadType);
        }
      } else {
        throw new Error(
          response?.responseMessage || "Failed to fetch leads data"
        );
      }
    } catch (error) {
      console.error(
        `Error loading ${activeTab === 0 ? "leads" : "eligibility"} data:`,
        error
      );
      setError(error.message);

      setData([]);

      setSnackbar({
        open: true,
        message: `Failed to load ${
          activeTab === 0 ? "leads" : "eligibility calculator"
          } data. Using sample data instead.`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const transformApiData = (apiData, leadType) => {
    if (leadType === "advisor") {
      return Array.isArray(apiData)
        ? apiData.map((lead) => ({
          id: lead.lead_id || lead.id,
          name: lead.name || "Unknown",
          email: lead.email || "No email",
          phone: lead.mobile_number || lead.mobileNumber,
          status: lead.status || "New",
          date: formatDate(lead.create_datetime || lead.createDatetime),
          source: lead.source || "Website",
          ...lead,
        }))
        : [];
    } else {
      return Array.isArray(apiData)
        ? apiData.map((lead) => {
          const transformedLead = {
            id: lead.lead_id || lead.id,
            phone: lead.mobile_number || lead.mobileNumber,
            status: lead.status || "Pending",
            date: formatDate(lead.create_datetime || lead.createDatetime),
            lead_type: lead.lead_type || "elical",
          };

          for (const key in lead) {
            if (Object.prototype.hasOwnProperty.call(lead, key)) {
              // Skip the excluded fields
              const excludedFieldsInTransform = [
                "id",
                "lead_id",
                "mobile_number",
                "mobileNumber",
                "create_datetime",
                "createDatetime",
                "Outstanding_default",
                "End_use",
                "outstanding_default",
                "end_use",
              ];

              if (!excludedFieldsInTransform.includes(key)) {
                // Skip longitude and latitude fields with undefined values
                const isGeoField =
                  key.toLowerCase().includes("longitude") ||
                  key.toLowerCase().includes("latitude");
                const isUndefinedValue =
                  lead[key] === undefined ||
                  lead[key] === null ||
                  lead[key] === "undefined" ||
                  String(lead[key]).toLowerCase() === "undefined";

                if (!(isGeoField && isUndefinedValue)) {
                  transformedLead[key] = lead[key];
                }
              }
            }
          }

          return transformedLead;
        })
        : [];
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";

    try {
      const date = new Date(dateString);
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return date.toLocaleDateString("en-US", options);
    } catch (e) {
      return dateString;
    }
  };

  const updateStats = (loadedData, leadType) => {
    if (leadType === "advisor") {
      const newStats = {
        total: loadedData.length,
        new: loadedData.filter((item) => item.status === "New").length,
        contacted: loadedData.filter((item) => item.status === "Contacted")
          .length,
        qualified: loadedData.filter((item) => item.status === "Qualified")
          .length,
      };
      setStats((prevStats) => ({ ...prevStats, ...newStats }));
    } else {
      const newStats = {
        total: loadedData.length,
        eligible: loadedData.filter((item) => item.status === "Eligible")
          .length,
        notEligible: loadedData.filter((item) => item.status === "Not Eligible")
          .length,
        pending: loadedData.filter((item) => item.status === "Pending").length,
      };
      setStats((prevStats) => ({ ...prevStats, ...newStats }));
    }
  };

  const resetStats = (leadType) => {
    if (leadType === "advisor") {
      setStats((prevStats) => ({
        ...prevStats,
        total: 0,
        new: 0,
        contacted: 0,
        qualified: 0,
      }));
    } else {
      setStats((prevStats) => ({
        ...prevStats,
        total: 0,
        eligible: 0,
        notEligible: 0,
        pending: 0,
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const pageable = {
      page: newPage,
      size: rowsPerPage,
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    const pageable = {
      page: 0,
      size: newSize,
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return theme.palette.info.main;
      case "Contacted":
        return theme.palette.secondary.main;
      case "Qualified":
        return theme.palette.success.main;
      case "Eligible":
        return theme.palette.success.main;
      case "Not Eligible":
        return theme.palette.error.main;
      case "Pending":
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase();
  };

  const handleViewClick = (row) => {
    setSelectedLead(row);
    setDetailsModalOpen(true);
  };


  const handleDeleteClick = (row) => {
    setLeadToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!leadToDelete) return;

    try {
      const response = await deleteLead(leadToDelete.id);

      if (response && response.responseCode === 200) {
        setSnackbar({
          open: true,
          message: `Lead deleted successfully`,
          severity: "success",
        });

        handleRefresh();
      } else {
        throw new Error(
          response?.responseMessage || "Failed to delete lead"
        );
      }
    } catch (error) {
      toast.error("Error deleting lead:", error);
      setSnackbar({
        open: true,
        message: `Failed to delete lead: ${error.message}`,
        severity: "error",
      });
    } finally {
      setDeleteConfirmationOpen(false);
      setLeadToDelete(null);
    }
  };

  const handleCloseDeleteConfirmation = () => {
    setDeleteConfirmationOpen(false);
    setLeadToDelete(null);
  };

  const handleRefresh = () => {
    resetStats(activeTab === 0 ? "advisor" : "elical");
    const pageable = {
      page,
      size: rowsPerPage,
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    loadData(pageable);
  };
  const handleUpdateStatus = async (leadId, newStatus) => {
    updateLeadStatus(leadId, newStatus)
      .then((response) => {
        if (response.responseCode === 200) {
          handleRefresh();
          showToast(`Lead status updated successfully`, "success");
        } else {
          showToast(
            `Failed to update status for lead: ${
              response?.responseMessage || "Unknown error"
            }`,
            "error"
          );
        }
      })
      .catch((error) => {
        console.error("Error updating lead status:", error);
        showToast(`Failed to update status for lead`, "error");
      });
  };

  // Utility function to download data as Excel
  const exportToExcel = async () => {
    const leadType = activeTab === 0 ? "advisor" : "elical";
    const pageable = {
      page: "",
      size: "",
      startDate: dateRange[0]
        ? formatToISODateTime(dateRange[0], "startDate")
        : "",
      endDate: dateRange[1] ? formatToISODateTime(dateRange[1], "endDate") : "",
    };
    const response = await getLeads(leadType, pageable);

    if (response && response.responseCode === 200 && response?.result) {
      const columnsToExport = columns.filter((col) => col.id !== "actions");
      const transformedData = transformApiData(response?.result, leadType);

      const headers = columnsToExport.map((col) => col.label || col.id);
      const rows = transformedData.map((row) =>
        columnsToExport.map((col) => {
          let value = row[col.id];
          if (col.render) value = col.render(row);
          return value ?? "-";
        })
      );

      const worksheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Apply yellow fill and bold font to header cells
      columnsToExport.forEach((_, index) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: index });
        if (!worksheet[cellAddress]) return;

        worksheet[cellAddress].s = {
          fill: {
            patternType: "solid",
            fgColor: { rgb: "FFFF00" }, // Yellow fill
          },
          font: {
            bold: true,
          },
        };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const fileName = `Leads_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const dataBlob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(dataBlob, fileName);
    }
  };

  const handleCreateLeadPopup = () => {
    if (activeTab === 0) {
      setShowTalkToAdvisorForm(true);
    } else {
      setShowEligibilityCalculator(true);
    }
  };

  const renderStatsCards = () => {
    return (
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Card sx={{ minWidth: 240, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total {activeTab === 0 ? "Leads" : "Applications"}
            </Typography>
            <Typography variant="h3" component="div" color="primary">
            {totalRecords}
              {/* {activeTab === 0 ? totalRecords : stats.total} */}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 240, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {activeTab === 0 ? "New Leads" : "Eligible"}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              color={activeTab === 0 ? "info" : "success"}
            >
              {activeTab === 0 ? stats.new : stats.eligible}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 240, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {activeTab === 0 ? "Contacted" : "Not Eligible"}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              color={activeTab === 0 ? "secondary" : "error"}
            >
              {activeTab === 0 ? stats.contacted : stats.notEligible}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ minWidth: 240, flex: 1 }}>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              {activeTab === 0 ? "Qualified" : "Pending Review"}
            </Typography>
            <Typography
              variant="h3"
              component="div"
              color={activeTab === 0 ? "success" : "warning"}
            >
              {activeTab === 0 ? stats.qualified : stats.pending}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 3,
          mb: 4,
        }}
      >
        {/* Title and Subtitle */}
        <Box>
          <Typography
            variant="h4"
            fontWeight="bold"
            color="primary"
            gutterBottom
          >
            Leads Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {activeTab === 0
              ? "Track and manage potential customer leads"
              : "View and manage eligibility calculation results"}
          </Typography>
        </Box>

        {/* Action Panel */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-end",
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Select a Date Range
            </Typography>
            <DateRangePickerWrapper value={dateRange} onChange={setDateRange} />
          </Box>
          {permission?.create===1&&(<Button
            variant="contained"
            startIcon={activeTab === 0 ? <PersonAddIcon /> : <CalculateIcon />}
            onClick={() => {
              handleCreateLeadPopup();
            }}
            sx={{ borderRadius: 2, minWidth: 160 }}
          >
            {activeTab === 0
              ? "Add New Lead"
              : "Add Eligibility Calculator Lead"}
          </Button>)}

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            Refresh
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{ borderRadius: 2, minWidth: 160 }}
          >
            Export to Excel
          </Button>
        </Box>
      </Box>

      {/* Stats and Alerts */}
      <Box sx={{ mt: 2 }}>
        {renderStatsCards()}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <TabContainer>
          <TabButton
            selected={activeTab === 0}
            onClick={() => handleTabChange(null, 0)}
          >
            Talk to Advisor
          </TabButton>
          <TabButton
            selected={activeTab === 1}
            onClick={() => handleTabChange(null, 1)}
          >
            Eligibility Calculator
          </TabButton>
        </TabContainer>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Name, Email, Mobile, or Company..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            maxWidth: 600,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "#f8f9fa",
              "&:hover": {
                backgroundColor: "#f0f1f2",
              },
              "&.Mui-focused": {
                backgroundColor: "white",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Data Table */}
      <Paper
        sx={{
          width: "100%",
          mb: 2,
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <CommonTable
          permissions={JSON.parse(userPermission.find(e => e.module === "leads")?.permission)}
          columns={columns}
          data={data}
          loading={loading}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          onDeleteClick={handleDeleteClick}
          onViewClick={handleViewClick}
          activeTab={activeTab}
          getStatusColor={getStatusColor}
          getInitials={getInitials}
          onSort={handleSort}
          currentSortBy={sortBy}
          currentOrder={order}
          noDataMessage={
            activeTab === 0
              ? "Try adding new leads or adjusting your filters"
              : "No eligibility calculations have been submitted yet for this section"
          }
          customCellRenderer={{
            contact:
              activeTab === 1
                ? (row) => (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{ mr: 2, bgcolor: theme.palette.primary.main }}
                    >
                      {getInitials(row.name || "")}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {row.name || "-"}
                    </Typography>
                  </Box>
                )
                : null,
            status: (row) => (
              <Chip
                label={row.status || "-"}
                sx={{
                  bgcolor: `${getStatusColor(row.status)}15`,
                  color: getStatusColor(row.status),
                  fontWeight: 600,
                }}
                size="small"
              />
            ),
            date: (row) => {
              if (!row.date) return "-";
              try {
                const dateObj = new Date(row.date);
                const formattedDate = dateObj.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
                const formattedTime = dateObj.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                return (
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {formattedDate}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formattedTime}
                    </Typography>
                  </Box>
                );
              } catch (e) {
                return row.date;
              }
            },
            eligibilityScore: (row) =>
              row.score !== undefined ? `${row.score}%` : "-",
          }}
          totalRecords={totalRecords}
          handleUpdateStatus={handleUpdateStatus}
        />
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal */}
      <ElicalLeadDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedLead(null);
        }}
        leadData={selectedLead}
      />

      <TalkToAdvisorLeadForm
        open={showTalkToAdvisorForm}
        onClose={() => setShowTalkToAdvisorForm(false)}
        onSuccess={handleRefresh}
      />
      <EligibilityCalculatorModal
        open={showEligibilityCalculator}
        onClose={() => setShowEligibilityCalculator(false)}
        onSuccess={handleRefresh}
      />

      <DeleteConfirmation
        open={deleteConfirmationOpen}
        handleClose={handleCloseDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        username={leadToDelete?.name || leadToDelete?.phone || "this lead"}
      />
    </Box>
  );
};

export default Leads;
